/**
 * K-S 檢定的理論臨界值（大樣本近似）。
 *
 * @param {number} n - 樣本數
 * @param {number} alpha - 顯著水準
 * @returns {number} - 臨界值
 */
function getKSCriticalValue(n, alpha) {
  const K = {
    0.2: 1.07,
    0.15: 1.14,
    0.1: 1.22,
    0.05: 1.36,
    0.01: 1.63,
  };

  if (!(alpha in K)) {
    throw new Error(`Unsupported alpha ${alpha}`);
  }

  return K[alpha] / Math.sqrt(n);
}

/**
 * Lilliefors 修正後的臨界值（近似查表）。
 *
 * @param {number} n - 樣本數
 * @param {number} alpha - 顯著水準
 * @returns {number} - 臨界值
 */
function getLillieforsCriticalValue(n, alpha) {
  const table = {
    20: { 0.2: 0.19, 0.15: 0.2, 0.1: 0.21, 0.05: 0.235, 0.01: 0.275 },
    25: { 0.2: 0.18, 0.15: 0.19, 0.1: 0.2, 0.05: 0.225, 0.01: 0.265 },
    30: { 0.2: 0.175, 0.15: 0.185, 0.1: 0.195, 0.05: 0.22, 0.01: 0.26 },
    40: { 0.2: 0.165, 0.15: 0.175, 0.1: 0.185, 0.05: 0.21, 0.01: 0.25 },
    50: { 0.2: 0.16, 0.15: 0.17, 0.1: 0.18, 0.05: 0.2, 0.01: 0.24 },
    60: { 0.2: 0.155, 0.15: 0.165, 0.1: 0.175, 0.05: 0.195, 0.01: 0.235 },
    70: { 0.2: 0.15, 0.15: 0.16, 0.1: 0.17, 0.05: 0.19, 0.01: 0.23 },
    100: { 0.2: 0.145, 0.15: 0.155, 0.1: 0.165, 0.05: 0.185, 0.01: 0.225 },
  };

  const closestN = Object.keys(table)
    .map(Number)
    .reduce((prev, curr) => (Math.abs(curr - n) < Math.abs(prev - n) ? curr : prev));

  const values = table[closestN];
  if (!values || !(alpha in values)) {
    throw new Error(`No Lilliefors critical value for n=${n}, alpha=${alpha}`);
  }

  return values[alpha];
}

/**
 * 檢查資料的常態分布特性，並提供 PDF / CDF 計算。
 *
 * @param {number[]} data - 數據陣列
 * @returns {{
 *   mean: number,
 *   stdDev: number,
 *   skewness: number,
 *   kurtosis: number,
 *   isNormal: boolean,
 *   pdf: (x: number) => number,
 *   cdf: (x: number) => number
 * }}
 */
export function checkNormalDistribution(data) {
  const n = data.length;
  if (n < 3) throw new Error('資料筆數過少，至少需 3 筆資料');

  const mean = data.reduce((sum, x) => sum + x, 0) / n;

  const variance = data.reduce((sum, x) => sum + (x - mean) ** 2, 0) / (n - 1);
  const stdDev = Math.sqrt(variance);

  const skewness =
    (n / ((n - 1) * (n - 2))) * data.reduce((sum, x) => sum + ((x - mean) / stdDev) ** 3, 0);

  const kurtosis =
    ((n * (n + 1)) / ((n - 1) * (n - 2) * (n - 3))) *
      data.reduce((sum, x) => sum + ((x - mean) / stdDev) ** 4, 0) -
    (3 * (n - 1) ** 2) / ((n - 2) * (n - 3));

  const isNormal = Math.abs(skewness) < 1 && Math.abs(kurtosis) < 1;

  // PDF
  const pdf = (x) => {
    const z = (x - mean) / stdDev;
    return (1 / (stdDev * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * z * z);
  };

  // Erf approximation
  const erf = (x) => {
    const sign = x < 0 ? -1 : 1;
    const absX = Math.abs(x);
    const t = 1 / (1 + 0.3275911 * absX);
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const y = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-absX * absX);
    return sign * y;
  };

  // CDF
  const cdf = (x) => 0.5 * (1 + erf((x - mean) / (stdDev * Math.sqrt(2))));

  return { mean, stdDev, skewness, kurtosis, isNormal, pdf, cdf };
}

/**
 * 使用 Kolmogorov-Smirnov 檢定資料是否為常態分布。
 * 可選擇是否使用 Lilliefors 修正。
 *
 * @param {number[]} data - 數據陣列
 * @param {{ alpha?: number, useLilliefors?: boolean }} options
 * @returns {{
 *   D: number,
 *   D_critical: number,
 *   passed: boolean,
 *   alpha: number,
 *   method: "KS" | "Lilliefors"
 * }}
 */
export function ksNormalTest(data, options = {}) {
  const { alpha = 0.05, useLilliefors = true } = options;
  const n = data.length;
  if (n < 4) throw new Error('樣本數至少須為 4');

  const sorted = [...data].sort((a, b) => a - b);
  const { cdf } = checkNormalDistribution(data);

  let D = 0;
  for (let i = 0; i < n; i++) {
    const xi = sorted[i];
    const fTheoretical = cdf(xi);
    const fEmpirical = (i + 1) / n;
    const dPlus = Math.abs(fEmpirical - fTheoretical);
    const dMinus = Math.abs(fTheoretical - i / n);
    D = Math.max(D, dPlus, dMinus);
  }

  const dCritical = useLilliefors
    ? getLillieforsCriticalValue(n, alpha)
    : getKSCriticalValue(n, alpha);

  return {
    D,
    dCritical,
    passed: D < dCritical,
    alpha,
    method: useLilliefors ? 'Lilliefors' : 'KS',
  };
}

/**
 * 計算製程能力指標 Cpk（Process Capability Index）的實用版本。
 * 當數據為常數時，使用最小可檢測變異性來計算 CPK。
 *
 * @param {number[]} data - 數據樣本
 * @param {number} USL - 上規格限（Upper Specification Limit）
 * @param {number} LSL - 下規格限（Lower Specification Limit）
 * @param {number} [minStdDev=0.001] - 最小標準差，用於常數數據
 * @returns {{
 *   mean: number,
 *   stdDev: number,
 *   CPU: number,
 *   CPL: number,
 *   CPK: number,
 *   isConstant: boolean
 * }}
 * @throws {Error} 當樣本數少於 2 或 USL <= LSL 時會拋出錯誤
 */
export function calculateCPK(data, USL, LSL, minStdDev = 0.001) {
  if (data.length < 2) {
    throw new Error('資料筆數需至少為 2 筆');
  }
  if (USL <= LSL) {
    throw new Error('上規格限 (USL) 必須大於下規格限 (LSL)');
  }

  const n = data.length;
  const mean = data.reduce((sum, x) => sum + x, 0) / n;

  const variance = data.reduce((sum, x) => sum + (x - mean) ** 2, 0) / (n - 1);
  let stdDev = Math.sqrt(variance);

  // 檢查是否所有值都相同（常數數據）
  const isConstant = stdDev === 0;

  // 如果是常數數據，使用最小標準差
  if (isConstant) {
    stdDev = minStdDev;
  }

  const CPU = (USL - mean) / (3 * stdDev);
  const CPL = (mean - LSL) / (3 * stdDev);
  const CPK = Math.min(CPU, CPL);

  return { mean, stdDev, CPU, CPL, CPK, isConstant };
}
